/**
 * Hong Kong mahjong rules core: tiles, winning hands, fan, and how far a
 * hand is from ready. Pure functions over tile-count arrays, shared by the
 * table (games/mahjong.ts), its AIs and the sim.
 *
 * Tiles are 0..33: 0-8 Characters (Man), 9-17 Dots, 18-26 Bamboo,
 * 27-30 the winds East South West North, 31-33 the dragons Red Green White.
 * Four of each, plus the eight bonus tiles, one each (user, 2026-09-27):
 * 34-37 the flowers Plum Orchid Chrysanthemum Bamboo, 38-41 the seasons
 * Spring Summer Autumn Winter. 144 in all. A bonus tile never stays in a
 * hand: it's set aside as it's drawn and scores at the end.
 */
export type Tile = number;
/** Tile kinds that can be in a hand (the bonus tiles never are). */
export const TILE_KINDS = 34;
export const EAST = 27;
export const RED = 31;
/** The first flower; the seasons follow at FLOWER + 4. */
export const FLOWER = 34;

export const isFlower = (t: Tile) => t >= FLOWER;
export const isHonor = (t: Tile) => t >= 27 && t < FLOWER;
export const isDragon = (t: Tile) => t >= 31 && t < FLOWER;
export const isWind = (t: Tile) => t >= 27 && t <= 30;
export const suitOf = (t: Tile) => (t >= 27 ? 3 : Math.floor(t / 9));
export const rankOf = (t: Tile) => (t % 9) + 1;
export const isTerminalOrHonor = (t: Tile) => isHonor(t) || rankOf(t) === 1 || rankOf(t) === 9;

/**
 * Tile symbols (user, 2026-09-23): 萬 for the characters suit, a dot for
 * Dots, a broken bar ¦ (a stick of bamboo) for Bamboo — "7●", "3萬", "9¦" —
 * and the real characters for the honors: winds 東 南 西 北, dragons 中
 * (Red) 發 (Green) and □ for White (its tile is blank). Not yet confirmed
 * on the G2's font; if they show as blanks or boxes on real hardware, flip
 * this to false for letters ("7D", "E", "Rd").
 */
export const USE_SUIT_SYMBOLS = true;
export const SUIT_LETTERS = USE_SUIT_SYMBOLS ? ['萬', '●', '¦'] : ['M', 'D', 'B'];
// The honors, then the flowers and seasons (梅 蘭 菊 竹, 春 夏 秋 冬).
const HONOR_NAMES = USE_SUIT_SYMBOLS
  ? ['東', '南', '西', '北', '中', '發', '□', '梅', '蘭', '菊', '竹', '春', '夏', '秋', '冬']
  : ['E', 'S', 'W', 'N', 'Rd', 'Gn', 'Wh', 'F1', 'F2', 'F3', 'F4', 'S1', 'S2', 'S3', 'S4'];
const HONOR_ENGLISH = ['East', 'South', 'West', 'North', 'Red', 'Green', 'White', 'Plum', 'Orchid', 'Chrysanthemum', 'Bamboo', 'Spring', 'Summer', 'Autumn', 'Winter'];
export const SUIT_NAMES = ['Man', 'Dots', 'Bam'];

/** Short name for the right pane: "7●", "3萬", "東", "中" (letters: "7D", "E", "Rd"). */
export function tileShort(t: Tile): string {
  return t >= 27 ? HONOR_NAMES[t - 27]! : `${rankOf(t)}${SUIT_LETTERS[suitOf(t)]}`;
}

/** Long name: "7●", "3萬", "西 West", "中 Red", "梅 Plum" (letters: "7 Bam", "West"). */
export function tileLong(t: Tile): string {
  if (t >= 27) return USE_SUIT_SYMBOLS ? `${HONOR_NAMES[t - 27]} ${HONOR_ENGLISH[t - 27]}` : HONOR_ENGLISH[t - 27]!;
  return USE_SUIT_SYMBOLS ? tileShort(t) : `${rankOf(t)} ${SUIT_NAMES[suitOf(t)]}`;
}

/** Just the face within its row: "7", "東", "中". */
export function tileFace(t: Tile): string {
  return t >= 27 ? HONOR_NAMES[t - 27]! : `${rankOf(t)}`;
}

export function fullWall(): Tile[] {
  const wall: Tile[] = [];
  for (let t = 0; t < TILE_KINDS; t++) for (let k = 0; k < 4; k++) wall.push(t);
  for (let t = FLOWER; t < FLOWER + 8; t++) wall.push(t);
  return wall;
}

export function countsOf(tiles: readonly Tile[]): number[] {
  const c = new Array<number>(TILE_KINDS).fill(0);
  for (const t of tiles) c[t]!++;
  return c;
}

export interface Meld {
  /** A kong is four of a kind: it scores as a pung, and its player draws a
   *  replacement tile. */
  kind: 'pung' | 'chow' | 'kong';
  /** The pung's or kong's tile, or a chow's lowest tile. */
  tile: Tile;
  /** Called from a discard (shown to everyone), or formed in the hand: a
   *  concealed kong is declared, but the hand still counts as concealed. */
  open: boolean;
}

export function meldTiles(m: Meld): Tile[] {
  if (m.kind === 'kong') return [m.tile, m.tile, m.tile, m.tile];
  return m.kind === 'pung' ? [m.tile, m.tile, m.tile] : [m.tile, m.tile + 1, m.tile + 2];
}

export function meldShort(m: Meld): string {
  if (m.kind === 'pung') return `Pung ${tileShort(m.tile)}`;
  if (m.kind === 'kong') return `Kong ${tileShort(m.tile)}`;
  return `Chow ${rankOf(m.tile)}${rankOf(m.tile) + 1}${rankOf(m.tile) + 2}${SUIT_LETTERS[suitOf(m.tile)]}`;
}

const canChowFrom = (t: Tile) => !isHonor(t) && rankOf(t) <= 7;

// ── Ready distance (shanten) ──

/**
 * How many tiles a hand is from ready: 0 = ready (one tile from winning),
 * -1 = already a winning hand. `counts` is the concealed part; `melds` sets
 * are already made. Takes the best of the standard shape, seven pairs and
 * thirteen orphans (the last two only with no melds).
 */
export function shanten(counts: number[], melds: number): number {
  let best = regularShanten(counts, melds);
  if (melds === 0) best = Math.min(best, sevenPairsShanten(counts), orphansShanten(counts));
  return best;
}

function regularShanten(counts: number[], melds: number): number {
  const c = [...counts];
  let best = 8;
  const dfs = (i: number, sets: number, partial: number, pair: number): void => {
    while (i < TILE_KINDS && c[i] === 0) i++;
    if (i >= TILE_KINDS) {
      const s = sets + melds;
      const p = Math.min(partial, 4 - s);
      best = Math.min(best, 8 - 2 * s - p - pair);
      return;
    }
    if (best <= -1) return;
    if (c[i]! >= 3) {
      c[i]! -= 3;
      dfs(i, sets + 1, partial, pair);
      c[i]! += 3;
    }
    if (canChowFrom(i) && c[i + 1]! > 0 && c[i + 2]! > 0) {
      c[i]!--, c[i + 1]!--, c[i + 2]!--;
      dfs(i, sets + 1, partial, pair);
      c[i]!++, c[i + 1]!++, c[i + 2]!++;
    }
    if (c[i]! >= 2) {
      c[i]! -= 2;
      if (!pair) dfs(i, sets, partial, 1);
      dfs(i, sets, partial + 1, pair);
      c[i]! += 2;
    }
    if (!isHonor(i) && rankOf(i) <= 8 && c[i + 1]! > 0) {
      c[i]!--, c[i + 1]!--;
      dfs(i, sets, partial + 1, pair);
      c[i]!++, c[i + 1]!++;
    }
    if (canChowFrom(i) && c[i + 2]! > 0) {
      c[i]!--, c[i + 2]!--;
      dfs(i, sets, partial + 1, pair);
      c[i]!++, c[i + 2]!++;
    }
    c[i]!--;
    dfs(i, sets, partial, pair);
    c[i]!++;
  };
  dfs(0, 0, 0, 0);
  return best;
}

function sevenPairsShanten(c: number[]): number {
  let pairs = 0;
  let kinds = 0;
  for (const n of c) {
    if (n > 0) kinds++;
    if (n >= 2) pairs++;
  }
  return 6 - pairs + Math.max(0, 7 - kinds);
}

const ORPHANS = [0, 8, 9, 17, 18, 26, 27, 28, 29, 30, 31, 32, 33];

function orphansShanten(c: number[]): number {
  let kinds = 0;
  let pair = 0;
  for (const t of ORPHANS) {
    if (c[t]! > 0) kinds++;
    if (c[t]! >= 2) pair = 1;
  }
  return 13 - kinds - pair;
}

// ── Winning hands and fan ──

export interface WinContext {
  /** Won on your own draw rather than a discard. */
  selfDraw: boolean;
  seatWind: Tile;
  roundWind: Tile;
  /** The flowers and seasons this player has set aside. */
  flowers?: readonly Tile[];
  /** Won on the replacement tile drawn after a kong. */
  kongDraw?: boolean;
}

export interface FanResult {
  fan: number;
  /** What scored ("Half flush 3", ...). */
  names: string[];
}

/** Fan needed to win, and the cap (limit hands score it). */
export const MIN_FAN = 3;
export const LIMIT_FAN = 8;

interface Shape {
  sets: Meld[];
  pair: Tile;
}

/** Every way the concealed tiles split into sets and one pair. */
function shapes(counts: number[], setsNeeded: number): Shape[] {
  const out: Shape[] = [];
  const c = [...counts];
  const rec = (i: number, sets: Meld[], pair: Tile | null): void => {
    while (i < TILE_KINDS && c[i] === 0) i++;
    if (i >= TILE_KINDS) {
      if (pair !== null && sets.length === setsNeeded) out.push({ sets: [...sets], pair });
      return;
    }
    if (pair === null && c[i]! >= 2) {
      c[i]! -= 2;
      rec(i, sets, i);
      c[i]! += 2;
    }
    if (c[i]! >= 3) {
      c[i]! -= 3;
      rec(i, [...sets, { kind: 'pung', tile: i, open: false }], pair);
      c[i]! += 3;
    }
    if (canChowFrom(i) && c[i + 1]! > 0 && c[i + 2]! > 0) {
      c[i]!--, c[i + 1]!--, c[i + 2]!--;
      rec(i, [...sets, { kind: 'chow', tile: i, open: false }], pair);
      c[i]!++, c[i + 1]!++, c[i + 2]!++;
    }
  };
  rec(0, [], null);
  return out;
}

/** Fan for one way of reading a winning hand. */
function scoreShape(shape: Shape, ctx: WinContext, concealed: boolean): FanResult {
  const sets = shape.sets;
  const all = [...sets.flatMap(meldTiles), shape.pair, shape.pair];
  const names: string[] = [];
  let fan = 0;
  const add = (name: string, n: number) => {
    names.push(`${name} ${n}`);
    fan += n;
  };
  // A kong scores as a pung.
  const pungOf = (t: Tile) => sets.some((m) => m.kind !== 'chow' && m.tile === t);
  const dragonPungs = [31, 32, 33].filter(pungOf).length;
  const windPungs = [27, 28, 29, 30].filter(pungOf).length;
  if (dragonPungs === 3) return { fan: LIMIT_FAN, names: ['Big three dragons'] };
  if (windPungs === 4) return { fan: LIMIT_FAN, names: ['Big four winds'] };
  if (all.every(isHonor)) return { fan: LIMIT_FAN, names: ['All honors'] };
  if (sets.every((m) => m.kind === 'kong')) return { fan: LIMIT_FAN, names: ['Four kongs'] };

  const suits = new Set(all.filter((t) => !isHonor(t)).map(suitOf));
  const honors = all.some(isHonor);
  if (suits.size === 1 && !honors) add('Full flush', 7);
  else if (suits.size === 1) add('Half flush', 3);
  if (sets.every((m) => m.kind !== 'chow')) add('All pungs', 3);
  else if (sets.every((m) => m.kind === 'chow')) add('All chows', 1);
  if (dragonPungs === 2 && isDragon(shape.pair)) add('Small dragons', 5);
  else if (dragonPungs) add('Dragon pung', dragonPungs);
  if (pungOf(ctx.seatWind)) add('Seat wind', 1);
  if (pungOf(ctx.roundWind)) add('Round wind', 1);
  if (ctx.selfDraw) add('Self-draw', 1);
  if (concealed) add('Concealed', 1);
  return { fan: Math.min(fan, LIMIT_FAN), names };
}

/**
 * The best fan for a complete hand (concealed tiles, winning tile included,
 * plus melds), or null if it isn't a winning shape. A win still needs
 * MIN_FAN; the caller checks that. Includes the bonus fan (bonusFan), which
 * counts toward the minimum as in standard Hong Kong rules.
 */
export function bestFan(concealed: readonly Tile[], melds: readonly Meld[], ctx: WinContext): FanResult | null {
  const r = handFan(concealed, melds, ctx);
  if (!r || r.fan >= LIMIT_FAN) return r;
  const bonus = bonusFan(ctx);
  return bonus.fan ? { fan: Math.min(r.fan + bonus.fan, LIMIT_FAN), names: [...r.names, ...bonus.names] } : r;
}

/**
 * The Hong Kong bonus fan on top of a winning hand (user, 2026-09-27): your
 * own flower and your own season, 1 fan each (East's are Plum and Spring,
 * South's Orchid and Summer, and so on); no flowers or seasons at all, 1;
 * all four flowers or all four seasons, 2 (in place of the own-tile fan in
 * that set); and winning on the replacement tile after a kong, 1.
 */
export function bonusFan(ctx: WinContext): FanResult {
  const names: string[] = [];
  let fan = 0;
  const flowers = ctx.flowers ?? [];
  const seat = ctx.seatWind - EAST;
  if (!flowers.length) (fan += 1), names.push('No flowers 1');
  for (const [base, set, own] of [[FLOWER, 'flowers', 'Own flower'], [FLOWER + 4, 'seasons', 'Own season']] as const) {
    const have = flowers.filter((t) => t >= base && t < base + 4);
    if (have.length === 4) (fan += 2), names.push(`All four ${set} 2`);
    else if (have.includes(base + seat)) (fan += 1), names.push(`${own} 1`);
  }
  if (ctx.kongDraw) (fan += 1), names.push('Win on a kong 1');
  return { fan, names };
}

/** The fan for the tiles themselves, before the bonus fan. */
function handFan(concealed: readonly Tile[], melds: readonly Meld[], ctx: WinContext): FanResult | null {
  const counts = countsOf(concealed);
  const isConcealed = melds.every((m) => !m.open);
  let best: FanResult | null = null;
  const consider = (r: FanResult) => {
    if (!best || r.fan > best.fan) best = r;
  };
  if (melds.length === 0) {
    if (orphansShanten(counts) === -1) return { fan: LIMIT_FAN, names: ['Thirteen orphans'] };
    if (counts.filter((n) => n === 2).length === 7) {
      // Seven pairs: its own 4 fan, plus flushes and the self-draw/concealed.
      const suits = new Set(concealed.filter((t) => !isHonor(t)).map(suitOf));
      const honors = concealed.some(isHonor);
      const names = ['Seven pairs 4'];
      let fan = 4;
      if (suits.size === 1 && !honors) (fan += 7), names.push('Full flush 7');
      else if (suits.size === 1) (fan += 3), names.push('Half flush 3');
      if (suits.size === 0) return { fan: LIMIT_FAN, names: ['All honors'] };
      if (ctx.selfDraw) (fan += 1), names.push('Self-draw 1');
      consider({ fan: Math.min(fan, LIMIT_FAN), names });
    }
  }
  for (const shape of shapes(counts, 4 - melds.length)) {
    consider(scoreShape({ sets: [...melds, ...shape.sets], pair: shape.pair }, ctx, isConcealed));
  }
  return best;
}

/** Tiles that would complete the hand, each with the fan it would score
 *  (on a discard; a self-draw adds 1). */
export function waits(concealed: readonly Tile[], melds: readonly Meld[], ctx: WinContext): { tile: Tile; fan: number }[] {
  const out: { tile: Tile; fan: number }[] = [];
  // A tile you hold all four of (in your hand or your melds) can't come.
  const counts = countsOf([...concealed, ...melds.flatMap(meldTiles)]);
  for (let t = 0; t < TILE_KINDS; t++) {
    if (counts[t]! >= 4) continue;
    const r = bestFan([...concealed, t], melds, ctx);
    if (r) out.push({ tile: t, fan: r.fan });
  }
  return out;
}

/** What a winning hand pays, in bets, by fan (3 fan = 1 bet, the limit = 8). */
export const FAN_PAY: Record<number, number> = { 3: 1, 4: 2, 5: 3, 6: 4, 7: 6, 8: 8 };
export const fanPay = (fan: number) => FAN_PAY[Math.min(fan, LIMIT_FAN)] ?? 0;
